const BYTES_PER_MB = 1024 * 1024
const BYTES_PER_GB = 1024 * 1024 * 1024

function toFixedNumber(value, divisor) {
  if (typeof value !== 'number') return null
  return Number((value / divisor).toFixed(2))
}

async function scanDirectory(directoryHandle, result, options) {
  const iterator = directoryHandle.values()

  while (true) {
    const next = await iterator.next()
    if (next.done) break

    const handle = next.value
    if (handle.kind === 'directory') {
      result.directoryCount += 1
      await scanDirectory(handle, result, options)
      continue
    }

    result.fileCount += 1
    try {
      const originFile = await handle.getFile()
      result.totalBytes += originFile.size
    } catch {
      result.unreadableFileCount += 1
    }

    if (options.onProgress && result.fileCount % options.progressEvery === 0) {
      options.onProgress({
        fileCount: result.fileCount,
        totalBytes: result.totalBytes,
        totalMB: toFixedNumber(result.totalBytes, BYTES_PER_MB)
      })
    }
  }
}

/**
 * 统计图片缓存目录及当前源站存储使用情况。
 * 文件大小通过逐个读取 OPFS 文件元数据计算，文件较多时可能耗时较长。
 */
export async function getOpfsImageCacheStats(options = {}) {
  const directoryName = options.directoryName || 'dir'
  const scanOptions = {
    onProgress: options.onProgress,
    progressEvery: options.progressEvery || 1000
  }
  const result = {
    supported: Boolean(navigator.storage && navigator.storage.getDirectory),
    directoryName,
    directoryExists: false,
    fileCount: 0,
    directoryCount: 0,
    unreadableFileCount: 0,
    totalBytes: 0,
    totalMB: 0,
    totalGB: 0,
    originUsageBytes: null,
    originUsageMB: null,
    originQuotaBytes: null,
    originQuotaGB: null,
    opfsUsageBytes: null,
    opfsUsageMB: null,
    quotaUsagePercent: null
  }

  if (!result.supported) return result

  if (navigator.storage.estimate) {
    const estimate = await navigator.storage.estimate()
    const usageDetails = estimate.usageDetails || {}

    result.originUsageBytes = typeof estimate.usage === 'number' ? estimate.usage : null
    result.originUsageMB = toFixedNumber(result.originUsageBytes, BYTES_PER_MB)
    result.originQuotaBytes = typeof estimate.quota === 'number' ? estimate.quota : null
    result.originQuotaGB = toFixedNumber(result.originQuotaBytes, BYTES_PER_GB)
    result.opfsUsageBytes = typeof usageDetails.fileSystem === 'number' ? usageDetails.fileSystem : null
    result.opfsUsageMB = toFixedNumber(result.opfsUsageBytes, BYTES_PER_MB)
    result.quotaUsagePercent = result.originUsageBytes !== null && result.originQuotaBytes
      ? Number((result.originUsageBytes / result.originQuotaBytes * 100).toFixed(2))
      : null
  }

  const root = await navigator.storage.getDirectory()
  let imageDirectory

  try {
    imageDirectory = await root.getDirectoryHandle(directoryName)
    result.directoryExists = true
  } catch (error) {
    if (error.name === 'NotFoundError') return result
    throw error
  }

  await scanDirectory(imageDirectory, result, scanOptions)
  result.totalMB = toFixedNumber(result.totalBytes, BYTES_PER_MB)
  result.totalGB = toFixedNumber(result.totalBytes, BYTES_PER_GB)

  return result
}

export async function printOpfsImageCacheStats(options = {}) {
  const stats = await getOpfsImageCacheStats(options)

  console.table({
    '浏览器支持 OPFS': stats.supported,
    '缓存目录存在': stats.directoryExists,
    '图片文件数': stats.fileCount,
    '子目录数': stats.directoryCount,
    '无法读取文件数': stats.unreadableFileCount,
    '图片缓存大小(MB)': stats.totalMB,
    '图片缓存大小(GB)': stats.totalGB,
    '源站总占用(MB)': stats.originUsageMB,
    '浏览器报告 OPFS 占用(MB)': stats.opfsUsageMB,
    '源站配额(GB)': stats.originQuotaGB,
    '配额使用率(%)': stats.quotaUsagePercent
  })

  return stats
}
